import { Check, Package } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { categoryMeta } from "@/data/catalog";
import { mediaUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { postJson, useAuth } from "@/lib/auth";

export const itemImage = (item) => mediaUrl(item.image_url) ?? categoryMeta(item.category).img;

/** "Yo lo tengo": adds/removes the piece from the signed-in visitor's inventory. */
export const HaveButton = ({ item, className, testid }) => {
  const { t } = useLang();
  const { member, setMember } = useAuth();
  const navigate = useNavigate();
  const has = !!member && member.item_ids?.includes(item.id);
  const toggle = async () => {
    if (!member) {
      toast(t("item.needLogin"), { action: { label: t("auth.login"), onClick: () => navigate("/entrar?next=/catalogo/revista") } });
      return;
    }
    try {
      const r = await postJson(`/members/me/items/${item.id}`, null, has ? "DELETE" : "PUT");
      setMember({ ...member, item_ids: r.item_ids });
      toast.success(t(has ? "item.removed" : "item.added", { name: item.name }));
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <button type="button" onClick={toggle} aria-pressed={has} className={`${className} ${has ? "have-on" : ""}`} data-testid={testid}>
      {has && <Check size={12} className="mr-1 inline" />}{t(has ? "item.haveOn" : "item.have")}
    </button>
  );
};

export const OwnedTag = ({ item, className = "" }) => {
  const { t } = useLang();
  return item.owned ? (
    <span className={`bk-owned ${className}`} data-testid={`owned-tag-${item.id}`}><Check size={11} /> {t("item.owned")}</span>
  ) : null;
};

const Specs = ({ item }) => {
  const { t } = useLang();
  return (
  <div className="w-72 text-left" data-testid={`item-hover-${item.id}`}>
    <p className="mono text-[10px] text-[var(--green)]">{item.manufacturer} · {item.year}</p>
    <p className="font-display mt-1 text-xl leading-tight">{item.name}</p>
    {item.description && <p className="mt-2 text-xs leading-relaxed text-[var(--muted)]">{item.description}</p>}
    <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
      {[[t("item.line"), item.line], [t("item.version"), item.version], ...(item.specs ?? []).map((s) => [s.label, s.value])].filter(([, v]) => v).map(([k, v]) => (
        <div key={k + v} className="contents"><dt className="text-[var(--muted)]">{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  </div>
  );
};

export const ItemCard = ({ item, onOpen }) => {
  const { t } = useLang();
  return (
  <div className="bk-item" data-testid={`catalog-item-${item.id}`}>
    <HoverCard openDelay={300} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" onClick={() => onOpen(item)} className="bk-item-main" data-testid={`catalog-item-open-${item.id}`}>
          <span className="bk-item-img">
            {item.image_url ? <img src={itemImage(item)} alt={item.name} loading="lazy" /> : <span className="bk-item-ph"><img src={itemImage(item)} alt="" loading="lazy" /><Package size={12} /></span>}
            <OwnedTag item={item} className="absolute left-1.5 top-1.5" />
          </span>
          <span className="bk-item-name">{item.name}</span>
          <span className="bk-item-meta">{item.manufacturer} · {item.year}</span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="right" className="hidden border-white/10 bg-[#15130f] text-[#efe8dc] lg:block">
        <Specs item={item} />
      </HoverCardContent>
    </HoverCard>
    <HaveButton item={item} className="bk-have" testid={`have-it-btn-${item.id}`} />
  </div>
  );
};
